import { useEffect, useRef, useState, useCallback } from "react";
import EmailAssetToolbar from "@/components/admin/EmailAssetToolbar";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { POLES } from "@/lib/poles";
import {
  Mail, FileText, Send, Plus, X, Edit3, Trash2, Eye,
  Loader2, CheckCircle2, AlertCircle, Languages, Users, Filter,
} from "lucide-react";

const TEMPLATE_TYPES = [
  { value: "transactional", label: "Transactionnel", icon: "✉️", desc: "Emails système (réception candidature, facture, etc.)" },
  { value: "prospect", label: "Prospect", icon: "📥", desc: "Séquence d'acquisition / cold outreach" },
  { value: "application", label: "Candidat", icon: "📝", desc: "Suivi des candidatures (accusé, entretien, décision)" },
  { value: "contact", label: "Contact CRM", icon: "👤", desc: "Message libre à un contact" },
  { value: "member", label: "Membre", icon: "👑", desc: "Communication aux membres actifs" },
  { value: "newsletter", label: "Newsletter", icon: "📰", desc: "Templates pour newsletters mensuelles" },
  { value: "event", label: "Événement", icon: "📅", desc: "Invitations / rappels / annulations événement" },
  { value: "formation", label: "Formation", icon: "🎓", desc: "Communications liées aux formations" },
  { value: "petition", label: "Pétition", icon: "📢", desc: "Communications pétition" },
  { value: "opportunity", label: "Opportunité", icon: "💼", desc: "Suivi d'opportunités business" },
];
const LANGS = [
  { value: "fr", label: "🇫🇷 Français" },
  { value: "en", label: "🇬🇧 English" },
  { value: "es", label: "🇪🇸 Español" },
  { value: "it", label: "🇮🇹 Italiano" },
  { value: "pt", label: "🇵🇹 Português" },
];
const ITEM_KINDS = [
  { value: "event", label: "📅 Événement" },
  { value: "formation", label: "🎓 Formation" },
  { value: "petition", label: "📢 Pétition" },
  { value: "form", label: "📝 Formulaire" },
  { value: "member_highlight", label: "⭐ Membre à la une" },
  { value: "top_member_of_month", label: "🏆 Top membre du mois (auto)" },
];

function emptyTemplate() { return { type: "transactional", slug: "", subject: "", body: "<p>Bonjour {{first_name}},</p>", lang: "fr", description: "", variables: ["first_name", "last_name", "company_name"], tags: [] }; }
function emptyCampaign() {
  return {
    title: "", intro: "Bonjour {{first_name}},\n\nDécouvrez les actualités du mois :",
    items: [], recipients: { mode: "newsletter_subscribers", categories: [], poles: [], tags: [], langs: [] },
    translate_per_lang: true, test_email: "",
  };
}

// ============================== TEMPLATES TAB ==============================

export function TemplatesTab() {
  const bodyRef = useRef(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyTemplate());
  const [preview, setPreview] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterLang, setFilterLang] = useState("all");
  const [search, setSearch] = useState("");
  const [translating, setTranslating] = useState(null);

  const load = useCallback(() => api.get("/admin/settings/email-templates").then((r) => setItems(r.data || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEdit(null); setForm(emptyTemplate()); setOpen(true); };
  const openEdit = (t) => { setEdit(t); setForm({ ...emptyTemplate(), ...t, variables: t.variables || [] }); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    if (!form.slug.trim() || !form.subject.trim() || !form.body.trim()) {
      toast.error("Slug, sujet et corps requis");
      return;
    }
    try {
      const payload = { ...form, slug: form.slug.trim().toLowerCase().replace(/\s+/g, "_") };
      if (edit) await api.patch(`/admin/settings/email-templates/${edit.id}`, payload);
      else await api.post("/admin/settings/email-templates", payload);
      toast.success(edit ? "Template mis à jour" : "Template créé");
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Supprimer "${t.slug}" (${t.lang}) ?`)) return;
    await api.delete(`/admin/settings/email-templates/${t.id}`); toast.success("Supprimé"); load();
  };
  const translate = async (t) => {
    if (t.lang !== "fr") { toast.error("La traduction part toujours du FR (langue source)"); return; }
    const targets = ["en", "es", "it", "pt"].filter((l) => !items.find((x) => x.slug === t.slug && x.lang === l));
    if (targets.length === 0) { toast.info("Déjà traduit dans toutes les langues principales (EN/ES/IT/PT)"); return; }
    if (!window.confirm(`Traduire "${t.slug}" en ${targets.join(", ").toUpperCase()} via Gemini ?`)) return;
    setTranslating(t.id);
    try {
      const r = await api.post(`/admin/settings/email-templates/${t.id}/translate`, { target_langs: targets });
      const created = (r.data?.created || []).length;
      toast.success(`${created} traduction${created > 1 ? "s" : ""} créée${created > 1 ? "s" : ""}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur traduction"); }
    finally { setTranslating(null); }
  };
  const doPreview = (t) => {
    // Render preview inline using a simple wrapper (no backend round-trip needed).
    const sample = { first_name: "Marie", last_name: "Dupré", company_name: "VineaTech", contact_name: "Marie Dupré", country: "France", email: "marie@example.com", pole: "Innovation" };
    const interp = (s) => (s || "").replace(/\{\{\s*([\w_]+)\s*\}\}/g, (_, k) => sample[k] ?? `{{${k}}}`);
    const html = `<html><body style="font-family:Inter,system-ui,sans-serif;padding:24px;max-width:680px;margin:auto">${interp(t.body)}</body></html>`;
    setPreview({ slug: t.slug, subject: interp(t.subject), html });
  };

  const filtered = items.filter((t) => {
    if (filterType !== "all" && (t.type || "transactional") !== filterType) return false;
    if (filterLang !== "all" && t.lang !== filterLang) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(t.slug || "").toLowerCase().includes(s) && !(t.subject || "").toLowerCase().includes(s) && !(t.description || "").toLowerCase().includes(s)) return false;
    }
    return true;
  });

  // Group by slug to show how many lang variants exist per template family
  const slugStats = items.reduce((acc, t) => {
    if (!t.slug) return acc;
    acc[t.slug] = acc[t.slug] || { langs: new Set(), type: t.type || "transactional" };
    acc[t.slug].langs.add(t.lang);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-sm text-neutral-600">Bibliothèque unifiée — utilisée par toutes les modules (Prospects, Candidats, Contacts, Newsletter, etc.). Variables&nbsp;: <code className="bg-neutral-100 px-1 rounded">{`{{first_name}}`}</code> <code className="bg-neutral-100 px-1 rounded">{`{{last_name}}`}</code> <code className="bg-neutral-100 px-1 rounded">{`{{company_name}}`}</code> <code className="bg-neutral-100 px-1 rounded">{`{{contact_name}}`}</code></p>
          <p className="mt-1 text-xs text-neutral-500">{items.length} variante{items.length > 1 ? "s" : ""} · {Object.keys(slugStats).length} template{Object.keys(slugStats).length > 1 ? "s" : ""} unique{Object.keys(slugStats).length > 1 ? "s" : ""}</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-4 py-2 text-sm font-semibold text-white hover:bg-wine-600" data-testid="tpl-new-btn"><Plus className="h-4 w-4" /> Nouveau template</button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (slug, sujet, description)" className="min-w-[220px] flex-1 rounded-full border border-neutral-200 px-4 py-1.5 text-xs" data-testid="tpl-search" />
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs" data-testid="tpl-filter-type">
          <option value="all">Tous les types ({items.length})</option>
          {TEMPLATE_TYPES.map((t) => {
            const n = items.filter((x) => (x.type || "transactional") === t.value).length;
            return <option key={t.value} value={t.value}>{t.icon} {t.label} ({n})</option>;
          })}
        </select>
        <select value={filterLang} onChange={(e) => setFilterLang(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs" data-testid="tpl-filter-lang">
          <option value="all">Toutes langues</option>
          {LANGS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full min-w-[800px] text-sm">
          <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
            <tr><th className="px-4 py-3 text-left">Slug</th><th className="px-4 py-3 text-left">Type</th><th className="px-4 py-3 text-left">Langue</th><th className="px-4 py-3 text-left">Sujet</th><th className="px-4 py-3 text-left">Couvert</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-neutral-500">Aucun template ne correspond à ces filtres.</td></tr>}
            {filtered.map((t) => {
              const coverage = slugStats[t.slug]?.langs || new Set();
              const tt = TEMPLATE_TYPES.find((x) => x.value === (t.type || "transactional"));
              return (
                <tr key={t.id} className="border-t border-neutral-100" data-testid={`tpl-row-${t.id}`}>
                  <td className="px-4 py-3"><div className="font-mono font-semibold text-navy">{t.slug}</div>{t.description && <div className="mt-0.5 text-[11px] text-neutral-500">{t.description}</div>}</td>
                  <td className="px-4 py-3 text-xs">{tt ? <span title={tt.desc}>{tt.icon} {tt.label}</span> : t.type}</td>
                  <td className="px-4 py-3 text-xs">{LANGS.find((l) => l.value === t.lang)?.label || t.lang}</td>
                  <td className="px-4 py-3 text-xs text-neutral-700 max-w-xs truncate" title={t.subject}>{t.subject}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-0.5">
                      {LANGS.map((l) => (
                        <span key={l.value} className={`inline-flex items-center gap-0.5 rounded px-1 text-[10px] ${coverage.has(l.value) ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-400"}`} title={coverage.has(l.value) ? `Disponible en ${l.label}` : `Manquant en ${l.label}`}>{l.value.toUpperCase()}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => doPreview(t)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-blue-50 hover:text-blue-700" title="Aperçu" data-testid={`tpl-preview-${t.id}`}><Eye className="h-3.5 w-3.5" /></button>
                      {t.lang === "fr" && (
                        <button onClick={() => translate(t)} disabled={translating === t.id} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-40" title="Traduire en EN/ES/IT/PT (Gemini)" data-testid={`tpl-translate-${t.id}`}>
                          {translating === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Languages className="h-3.5 w-3.5" />}
                        </button>
                      )}
                      <button onClick={() => openEdit(t)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-blue-50 hover:text-blue-700" title="Modifier" data-testid={`tpl-edit-${t.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                      <button onClick={() => remove(t)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-rose-50 hover:text-rose-700" title="Supprimer" data-testid={`tpl-del-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <form onSubmit={save} className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tpl-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-navy">{edit ? "Modifier template" : "Nouveau template"}</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-neutral-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Slug (identifiant technique) *</label>
                <input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="ex: prospect_intro" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm font-mono" data-testid="tpl-slug" />
                <p className="mt-0.5 text-[10px] text-neutral-400">Toutes les langues partagent le même slug.</p>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Type *</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="tpl-type">
                  {TEMPLATE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Langue *</label>
                <select value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="tpl-lang">
                  {LANGS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Description</label>
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="À quoi sert ce template ?" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="tpl-desc" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Sujet *</label>
                <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Sujet (peut contenir {{first_name}} ou {{company_name}})" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="tpl-subject" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Corps HTML *</label>
                <textarea ref={bodyRef} required rows={12} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="<p>Bonjour {{first_name}},</p>" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm font-mono" data-testid="tpl-body" />
                <EmailAssetToolbar textareaRef={bodyRef} value={form.body} onChange={(v) => setForm((f) => ({ ...f, body: v }))} testidPrefix="tpl-asset" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
              <button type="submit" className="rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600" data-testid="tpl-save">{edit ? "Mettre à jour" : "Créer"}</button>
            </div>
          </form>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setPreview(null)}>
          <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tpl-preview-modal">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-navy">Aperçu — {preview.slug}</h3>
                <p className="text-xs text-neutral-500">Sujet : <strong>{preview.subject}</strong></p>
              </div>
              <button onClick={() => setPreview(null)} className="text-neutral-400"><X className="h-5 w-5" /></button>
            </div>
            <iframe title="preview" srcDoc={preview.html} className="mt-4 h-[60vh] w-full rounded-xl border border-neutral-200" />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================== NEWSLETTER COMPOSE TAB ==============================

export function NewsletterTab() {
  const introRef = useRef(null);
  const [campaigns, setCampaigns] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyCampaign());
  const [events, setEvents] = useState([]);
  const [formations, setFormations] = useState([]);
  const [petitions, setPetitions] = useState([]);
  const [forms, setForms] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [recipientsPreview, setRecipientsPreview] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [status, setStatus] = useState(null);

  const load = useCallback(() => api.get("/admin/newsletter-campaigns").then((r) => setCampaigns(r.data || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data || [])).catch(() => {});
    api.get("/formations").then((r) => setFormations(r.data?.items || r.data || [])).catch(() => {});
    api.get("/petitions").then((r) => setPetitions(r.data || [])).catch(() => {});
    api.get("/admin/forms").then((r) => setForms((r.data || []).filter((f) => f.status === "published"))).catch(() => {});
    api.get("/admin/companies", { params: { per_page: 200 } }).then((r) => setCompanies(r.data?.items || r.data || [])).catch(() => {});
    api.get("/public/membership-pricing").then((r) => setCategories(r.data || [])).catch(() => {});
    api.get("/admin/contacts/tags").then((r) => setTags(r.data || [])).catch(() => {});
  }, []);

  const openNew = () => { setEdit(null); setForm(emptyCampaign()); setOpen(true); setRecipientsPreview(null); };
  const openEdit = (c) => {
    setEdit(c);
    setForm({
      title: c.title || "", intro: c.intro || "",
      items: c.items || [], recipients: c.recipients || { mode: "newsletter_subscribers" },
      translate_per_lang: c.translate_per_lang !== false, test_email: c.test_email || "",
    });
    setOpen(true);
    setRecipientsPreview(null);
  };

  const save = async (e) => {
    e?.preventDefault();
    try {
      if (edit) {
        await api.patch(`/admin/newsletter-campaigns/${edit.id}`, form);
        toast.success("Campagne mise à jour");
      } else {
        const r = await api.post("/admin/newsletter-campaigns", form);
        setEdit(r.data);
        toast.success("Campagne créée");
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer "${c.title}" ?`)) return;
    await api.delete(`/admin/newsletter-campaigns/${c.id}`); toast.success("Supprimée"); load();
  };

  const addItem = (kind) => setForm({ ...form, items: [...form.items, { kind, ref_id: "", note: "" }] });
  const updateItem = (idx, patch) => setForm({ ...form, items: form.items.map((it, i) => i === idx ? { ...it, ...patch } : it) });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const previewRecipients = async () => {
    try {
      const r = await api.post("/admin/newsletter-campaigns/preview-recipients", form.recipients);
      setRecipientsPreview(r.data);
    } catch { toast.error("Erreur calcul destinataires"); }
  };

  const doPreview = async () => {
    if (!edit) { await save(); }
    const id = edit?.id;
    if (!id) return;
    try {
      const r = await api.post(`/admin/newsletter-campaigns/${id}/preview`);
      setPreviewHtml({ subject: r.data.subject, html: r.data.html });
    } catch { toast.error("Erreur preview"); }
  };

  const sendTest = async () => {
    if (!edit) { await save(); return toast("Sauvegardez à nouveau pour envoyer le test"); }
    if (!form.test_email) return toast.error("Renseignez un email de test");
    try {
      // Save current state first
      await api.patch(`/admin/newsletter-campaigns/${edit.id}`, form);
      const r = await api.post(`/admin/newsletter-campaigns/${edit.id}/send-test`);
      toast.success(`Test envoyé à ${r.data.to}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const sendCampaign = async () => {
    if (!edit) return toast.error("Sauvegardez d'abord");
    if (!window.confirm(`Envoyer cette campagne ? Les emails partiront par lots de 50.`)) return;
    try {
      await api.patch(`/admin/newsletter-campaigns/${edit.id}`, form);
      await api.post(`/admin/newsletter-campaigns/${edit.id}/send`);
      toast.success("Envoi lancé en arrière-plan");
      load();
      pollStatus(edit.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const pollStatus = (cid) => {
    const tick = async () => {
      try {
        const r = await api.get(`/admin/newsletter-campaigns/${cid}/status`);
        setStatus(r.data);
        if (r.data.status === "queued" || r.data.status === "sending") {
          setTimeout(tick, 3000);
        } else {
          load();
        }
      } catch { /* poll error ignored */ }
    };
    tick();
  };

  const togglePole = (id) => {
    const next = (form.recipients.poles || []).includes(id)
      ? form.recipients.poles.filter((x) => x !== id)
      : [...(form.recipients.poles || []), id];
    setForm({ ...form, recipients: { ...form.recipients, poles: next } });
  };
  const toggleCategory = (id) => {
    const next = (form.recipients.categories || []).includes(id)
      ? form.recipients.categories.filter((x) => x !== id)
      : [...(form.recipients.categories || []), id];
    setForm({ ...form, recipients: { ...form.recipients, categories: next } });
  };
  const toggleTag = (tag) => {
    const next = (form.recipients.tags || []).includes(tag)
      ? form.recipients.tags.filter((x) => x !== tag)
      : [...(form.recipients.tags || []), tag];
    setForm({ ...form, recipients: { ...form.recipients, tags: next } });
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-neutral-600">Composez vos newsletters en combinant événements, formations, pétitions, membre à la une et top membre du mois. Envoi par lots de 50, traduction automatique par langue du destinataire.</p>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-4 py-2 text-sm font-semibold text-white hover:bg-wine-600" data-testid="newsletter-new-btn"><Plus className="h-4 w-4" /> Nouvelle campagne</button>
      </div>

      {/* Campaigns list */}
      <div className="mt-5 overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full min-w-[700px] text-sm">
          <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
            <tr><th className="px-4 py-3 text-left">Titre</th><th className="px-4 py-3 text-left">Statut</th><th className="px-4 py-3 text-left">Envoyés</th><th className="px-4 py-3 text-left">Créée</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {campaigns.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-500">Aucune campagne</td></tr>}
            {campaigns.map((c) => (
              <tr key={c.id} className="border-t border-neutral-100" data-testid={`campaign-row-${c.id}`}>
                <td className="px-4 py-3 font-semibold text-navy">{c.title}</td>
                <td className="px-4 py-3">
                  {c.status === "sent" && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700"><CheckCircle2 className="mr-0.5 inline h-2.5 w-2.5" /> Envoyée</span>}
                  {c.status === "sending" && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700"><Loader2 className="mr-0.5 inline h-2.5 w-2.5 animate-spin" /> Envoi en cours</span>}
                  {c.status === "queued" && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">En file</span>}
                  {c.status === "failed" && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-700" title={c.error}><AlertCircle className="mr-0.5 inline h-2.5 w-2.5" /> Échec</span>}
                  {(!c.status || c.status === "draft") && <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold text-neutral-600">Brouillon</span>}
                </td>
                <td className="px-4 py-3 text-xs">{c.sent || 0} / {c.total || 0} {c.failed > 0 && <span className="text-rose-600">· {c.failed} échec</span>}</td>
                <td className="px-4 py-3 text-xs text-neutral-500">{(c.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(c)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-blue-50 hover:text-blue-700" title="Éditer" data-testid={`campaign-edit-${c.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(c)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-rose-50 hover:text-rose-700" title="Supprimer" data-testid={`campaign-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Composer modal */}
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="campaign-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-navy">{edit ? "Éditer campagne" : "Nouvelle campagne"}</h3>
              <button onClick={() => setOpen(false)} className="text-neutral-400"><X className="h-5 w-5" /></button>
            </div>

            <div className="mt-4 grid gap-4">
              {/* Title + intro */}
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre de la campagne (sera le sujet de l'email)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="campaign-title" />
              <div>
              <textarea ref={introRef} rows={5} value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} placeholder="Texte d'intro (variables {{first_name}})" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="campaign-intro" />
              <EmailAssetToolbar textareaRef={introRef} value={form.intro} onChange={(v) => setForm((f) => ({ ...f, intro: v }))} testidPrefix="campaign-asset" />
              </div>

              {/* Items */}
              <div className="rounded-xl border border-neutral-100 bg-neutral-50 p-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-navy">Contenus à inclure</h4>
                  <div className="flex flex-wrap gap-1">
                    {ITEM_KINDS.map((k) => (
                      <button key={k.value} onClick={() => addItem(k.value)} className="rounded-full bg-white border border-neutral-200 px-2.5 py-1 text-[11px] font-semibold hover:bg-wine-50" data-testid={`add-item-${k.value}`}>+ {k.label}</button>
                    ))}
                  </div>
                </div>
                {form.items.length === 0 && <p className="mt-3 text-xs italic text-neutral-400">Aucun contenu — utilisez les boutons ci-dessus pour ajouter événements, formations, pétitions, etc.</p>}
                <div className="mt-3 space-y-2">
                  {form.items.map((it, idx) => (
                    <div key={idx} className="flex items-center gap-2 rounded-lg border border-neutral-100 bg-white p-2" data-testid={`campaign-item-${idx}`}>
                      <span className="text-xs font-semibold text-navy w-32">{ITEM_KINDS.find((k) => k.value === it.kind)?.label}</span>
                      {it.kind === "event" && (
                        <select value={it.ref_id} onChange={(e) => updateItem(idx, { ref_id: e.target.value })} className="flex-1 rounded-lg border border-neutral-200 px-2 py-1 text-xs" data-testid={`item-event-select-${idx}`}>
                          <option value="">— Choisir un événement —</option>
                          {events.map((e) => <option key={e.id} value={e.id}>{e.title} ({(e.date || "").slice(0, 10)})</option>)}
                        </select>
                      )}
                      {it.kind === "formation" && (
                        <select value={it.ref_id} onChange={(e) => updateItem(idx, { ref_id: e.target.value })} className="flex-1 rounded-lg border border-neutral-200 px-2 py-1 text-xs" data-testid={`item-formation-select-${idx}`}>
                          <option value="">— Choisir une formation —</option>
                          {formations.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
                        </select>
                      )}
                      {it.kind === "petition" && (
                        <select value={it.ref_id} onChange={(e) => updateItem(idx, { ref_id: e.target.value })} className="flex-1 rounded-lg border border-neutral-200 px-2 py-1 text-xs" data-testid={`item-petition-select-${idx}`}>
                          <option value="">— Choisir une pétition —</option>
                          {petitions.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                        </select>
                      )}
                      {it.kind === "form" && (
                        <select value={it.ref_id} onChange={(e) => updateItem(idx, { ref_id: e.target.value })} className="flex-1 rounded-lg border border-neutral-200 px-2 py-1 text-xs" data-testid={`item-form-select-${idx}`}>
                          <option value="">— Choisir un formulaire —</option>
                          {forms.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
                        </select>
                      )}
                      {it.kind === "member_highlight" && (
                        <select value={it.ref_id} onChange={(e) => updateItem(idx, { ref_id: e.target.value })} className="flex-1 rounded-lg border border-neutral-200 px-2 py-1 text-xs" data-testid={`item-member-select-${idx}`}>
                          <option value="">— Choisir un membre —</option>
                          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      )}
                      {it.kind === "top_member_of_month" && <span className="flex-1 text-xs italic text-neutral-500">(Calculé automatiquement au moment de l&apos;envoi)</span>}
                      <input value={it.note || ""} onChange={(e) => updateItem(idx, { note: e.target.value })} placeholder="Mot perso (optionnel)" className="w-48 rounded-lg border border-neutral-200 px-2 py-1 text-xs" data-testid={`item-note-${idx}`} />
                      <button onClick={() => removeItem(idx)} className="grid h-7 w-7 place-items-center rounded text-rose-500 hover:bg-rose-50" data-testid={`item-remove-${idx}`}><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recipients */}
              <div className="rounded-xl border border-neutral-100 bg-neutral-50 p-4">
                <h4 className="font-bold text-navy"><Users className="mr-1 inline h-4 w-4" /> Destinataires</h4>
                <div className="mt-2 flex flex-wrap gap-3 text-sm" data-testid="recipients-mode">
                  <label className="inline-flex items-center gap-1.5"><input type="radio" name="rec-mode" checked={form.recipients.mode === "newsletter_subscribers"} onChange={() => setForm({ ...form, recipients: { ...form.recipients, mode: "newsletter_subscribers" } })} data-testid="rec-mode-newsletter" /> Tous les abonnés newsletter</label>
                  <label className="inline-flex items-center gap-1.5"><input type="radio" name="rec-mode" checked={form.recipients.mode === "by_filter"} onChange={() => setForm({ ...form, recipients: { ...form.recipients, mode: "by_filter" } })} data-testid="rec-mode-filter" /> Filtré (catégorie / pôle / tag){tags.length > 0 && <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700" data-testid="tags-available-badge">{tags.length} tag{tags.length > 1 ? "s" : ""} dispo</span>}</label>
                </div>

                {form.recipients.mode === "by_filter" && (
                  <>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <div>
                      <div className="text-[10px] font-bold uppercase text-neutral-500">Catégories d&apos;adhésion</div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {categories.map((c) => {
                          const active = (form.recipients.categories || []).includes(c.id);
                          return <button key={c.id} type="button" onClick={() => toggleCategory(c.id)} className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${active ? "border-wine-500 bg-wine-500 text-white" : "border-neutral-200 bg-white hover:bg-wine-50"}`} data-testid={`rec-cat-${c.id}`}>{c.name || c.label || c.id}</button>;
                        })}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase text-neutral-500">Pôles</div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {POLES.map((p) => {
                          const active = (form.recipients.poles || []).includes(p.id);
                          return <button key={p.id} type="button" onClick={() => togglePole(p.id)} className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${active ? "border-wine-500 bg-wine-500 text-white" : "border-neutral-200 bg-white hover:bg-wine-50"}`} data-testid={`rec-pole-${p.id}`}>{p.label}</button>;
                        })}
                      </div>
                    </div>
                  </div>
                  {tags.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] font-bold uppercase text-neutral-500">Tags / segments <span className="ml-1 font-normal text-neutral-400">(sélectionner un ou plusieurs)</span></div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {tags.map((t) => {
                          const active = (form.recipients.tags || []).includes(t.tag);
                          return (
                            <button key={t.tag} type="button" onClick={() => toggleTag(t.tag)} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${active ? "border-emerald-500 bg-emerald-500 text-white" : "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"}`} data-testid={`rec-tag-${t.tag}`}>
                              {t.label}
                              <span className={`rounded-full px-1.5 text-[9px] ${active ? "bg-white/25" : "bg-white"}`}>{t.count}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  </>
                )}

                <div className="mt-3 flex items-center justify-between">
                  <label className="inline-flex items-center gap-2 text-xs text-neutral-700"><input type="checkbox" checked={!!form.translate_per_lang} onChange={(e) => setForm({ ...form, translate_per_lang: e.target.checked })} className="accent-wine-500" data-testid="campaign-translate" /> <Languages className="h-3.5 w-3.5" /> Traduire automatiquement selon la langue du destinataire</label>
                  <button type="button" onClick={previewRecipients} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-3 py-1 text-xs font-bold hover:bg-neutral-50" data-testid="preview-recipients-btn"><Filter className="h-3 w-3" /> Calculer les destinataires</button>
                </div>
                {recipientsPreview && (
                  <div className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs" data-testid="rec-preview">
                    <strong>{recipientsPreview.total}</strong> destinataires.
                    {Object.keys(recipientsPreview.by_lang || {}).length > 0 && (
                      <span> Par langue : {Object.entries(recipientsPreview.by_lang).map(([l, n]) => `${l}: ${n}`).join(", ")}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Test send */}
              <div className="rounded-xl border border-neutral-100 bg-neutral-50 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <input type="email" value={form.test_email} onChange={(e) => setForm({ ...form, test_email: e.target.value })} placeholder="Email pour test" className="flex-1 rounded-lg border border-neutral-200 px-2 py-1 text-xs" data-testid="campaign-test-email" />
                  <button onClick={sendTest} className="inline-flex items-center gap-1.5 rounded-full bg-blue-500 px-3 py-1 text-xs font-bold text-white hover:bg-blue-600" data-testid="campaign-send-test"><Send className="h-3 w-3" /> Envoyer un test</button>
                  <button onClick={doPreview} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-3 py-1 text-xs font-bold hover:bg-neutral-50" data-testid="campaign-preview-btn"><Eye className="h-3 w-3" /> Aperçu</button>
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2 border-t border-neutral-100 pt-4">
              <button onClick={() => setOpen(false)} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Fermer</button>
              <button onClick={save} className="rounded-full bg-neutral-700 px-4 py-2 text-sm font-bold text-white hover:bg-neutral-800" data-testid="campaign-save">{edit ? "Enregistrer" : "Créer la campagne"}</button>
              <button onClick={sendCampaign} disabled={!edit} className="rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="campaign-send"><Send className="mr-1 inline h-3.5 w-3.5" /> Envoyer la campagne</button>
            </div>

            {status && (status.status === "queued" || status.status === "sending") && (
              <div className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-xs">
                <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Envoi en cours : {status.sent || 0} / {status.total || 0}
              </div>
            )}
          </div>
        </div>
      )}

      {previewHtml && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4" onClick={() => setPreviewHtml(null)}>
          <div className="w-full max-w-3xl rounded-2xl bg-white p-5 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-navy">Aperçu — {previewHtml.subject}</h3>
              <button onClick={() => setPreviewHtml(null)} className="text-neutral-400"><X className="h-5 w-5" /></button>
            </div>
            <iframe title="campaign-preview" srcDoc={previewHtml.html} className="mt-3 h-[60vh] w-full rounded-xl border border-neutral-200" />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================== MAIN ==============================

export default function AdminEmailings() {
  const [tab, setTab] = useState("templates");
  return (
    <PortalLayout sidebar="admin">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl"><Mail className="mr-2 inline h-7 w-7 text-wine-500" />Emailings</h1>
          <p className="mt-1 text-neutral-600">Templates d&apos;emails transactionnels + composeur de newsletter multilingue.</p>
        </div>
      </div>

      <div className="mt-6 flex gap-1 border-b border-neutral-200">
        <button onClick={() => setTab("templates")} className={`relative px-5 py-2.5 text-sm font-semibold ${tab === "templates" ? "text-wine-700" : "text-neutral-500 hover:text-navy"}`} data-testid="tab-templates">
          <FileText className="mr-1 inline h-4 w-4" /> Templates
          {tab === "templates" && <span className="absolute bottom-0 left-0 h-0.5 w-full bg-wine-500" />}
        </button>
        <button onClick={() => setTab("newsletter")} className={`relative px-5 py-2.5 text-sm font-semibold ${tab === "newsletter" ? "text-wine-700" : "text-neutral-500 hover:text-navy"}`} data-testid="tab-newsletter">
          <Send className="mr-1 inline h-4 w-4" /> Campagnes newsletter
          {tab === "newsletter" && <span className="absolute bottom-0 left-0 h-0.5 w-full bg-wine-500" />}
        </button>
      </div>

      <div className="mt-6">
        {tab === "templates" ? <TemplatesTab /> : <NewsletterTab />}
      </div>
    </PortalLayout>
  );
}
